import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../../contexts/strings';
import StoryCard from '../story-card';
import { Story, StoryKeys } from '../../types';
import TextInputWithValidate, {
  TextInputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import InputAdornment from '@mui/material/InputAdornment';
import { LoadingButton } from '@mui/lab';
import { useFotbollPlayValidate } from '../../hooks/validate';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';

const { REACT_APP_FOTBOLLPLAY_BASE_URL } = process.env;

interface Props
  extends Pick<TextInputWithValidateProps, 'onValue' | 'onValidation' | 'hideValidationError'> {
  selectedStory: Story | null;
  selectedLeague: string;
}

const videoKey: StoryKeys = 'videoUrl';
const videoAvailableKey = 'videoAvailable';

const StoryMediaCard = ({
  selectedStory,
  selectedLeague,
  onValue,
  onValidation,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const { fetchAvailableInPortrait, fetchAvailableWithoutPayment } = useFotbollPlayValidate();
  const [isLoading, setIsLoading] = useState(false);
  const [validateErrorMessage, setValidationErrorMessage] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(selectedStory?.[videoKey] || '');

  const onVideoUrlValue = useCallback(
    (key: string, value: string | null) => {
      setVideoUrl(value);
      onValue && onValue(key, value);
    },
    [onValue]
  );

  const onValidateUrl = useCallback(async () => {
    if (!videoUrl) {
      return;
    }

    try {
      setIsLoading(true);

      let isAvailableInPortraitValid = false;
      let isAvailableWithoutPaymentValid = false;

      const isFotbollPlayUrlValid = videoUrl.startsWith(REACT_APP_FOTBOLLPLAY_BASE_URL as string);
      const videoId = videoUrl.split('/').pop();

      if (!isFotbollPlayUrlValid || !videoId) {
        setValidationErrorMessage(strings.validations.validFotbollPlayUrl);
      } else {
        const availableInPortraitData = await fetchAvailableInPortrait(videoId, selectedLeague);
        const availableWithPaymentData = await fetchAvailableWithoutPayment(
          videoId,
          selectedLeague
        );

        const booleanSchema = yup.boolean().required().isTrue();

        isAvailableInPortraitValid = await booleanSchema.isValid(
          availableInPortraitData?.videoIsAvailableInPortrait
        );
        isAvailableWithoutPaymentValid = await booleanSchema.isValid(
          availableWithPaymentData?.videoAvailableWithoutPayment
        );

        if (!isAvailableInPortraitValid) {
          setValidationErrorMessage(strings.validations.validFotbollPlayVideoPortrait);
        } else if (!isAvailableWithoutPaymentValid) {
          setValidationErrorMessage(strings.validations.validFotbollPlayVideoPayment);
        } else {
          setValidationErrorMessage(null);
        }
      }

      const isValid =
        isFotbollPlayUrlValid && isAvailableInPortraitValid && isAvailableWithoutPaymentValid;

      onValidation && onValidation(videoAvailableKey, isValid);
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  }, [
    fetchAvailableInPortrait,
    fetchAvailableWithoutPayment,
    selectedLeague,
    onValidation,
    strings,
    videoUrl,
  ]);

  return (
    <StoryCard title={strings.stories.mediaTitle}>
      <Box>
        <TextInputWithValidate
          name={videoKey}
          validation={yup.string().required(strings.validations.isRequired)}
          label={strings.stories.mediaVideoLabel}
          helperText={!validateErrorMessage ? strings.stories.mediaVideoHelperText : ''}
          value={selectedStory?.[videoKey] || ''}
          onValue={onVideoUrlValue}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <LoadingButton variant="outlined" onClick={onValidateUrl} loading={isLoading}>
                  {strings.actions.validate}
                </LoadingButton>
              </InputAdornment>
            ),
          }}
        />
        {validateErrorMessage && (
          <FormHelperText error sx={{ marginLeft: '3px' }}>
            {validateErrorMessage}
          </FormHelperText>
        )}
      </Box>
    </StoryCard>
  );
};

export default StoryMediaCard;
